import { NgIf } from '@angular/common';
import { HttpContext } from '@angular/common/http';
import { Component, ElementRef, Input, ViewChild, computed, inject } from '@angular/core';
import { RouterLink } from '@angular/router';
import { TenantUserRoles } from '@buspro/shared/common';
import { GroupedOperationsDto, OperationsApi, TenantUsersDto } from '@buspro/web/apis';
import { CacheKeysEnum } from '@buspro/web/core';
import { ThemeMode, ThemeService } from '@buspro/web/core';
import { NGX_LOADING_BAR_IGNORED } from '@ngx-loading-bar/http-client';
import { injectQuery } from '@tanstack/angular-query-experimental';
import { NzBadgeModule } from 'ng-zorro-antd/badge';
import { NzDropDownDirective, NzDropdownMenuComponent } from 'ng-zorro-antd/dropdown';
import { NzTagComponent } from 'ng-zorro-antd/tag';
import { AvatarModule } from 'ngx-avatars';
import { lastValueFrom } from 'rxjs';
import { CommandPaletteService } from '../../services/command-palette.service';
import { AvailableBusesWidgetComponent } from './available-buses-widget/available-buses-widget.component';

interface PresenceMember {
  id: string;
  info: {
    name: string;
    tenantId: string;
    tenantName: string;
    terminalId?: string | null;
    role?: string;
  };
}

interface TenantPresence {
  tenantId: string;
  tenantName: string;
  onlineUsers: PresenceMember[];
}

@Component({
  selector: 'tenant-workspace-header',
  standalone: true,
  imports: [
    NgIf,
    RouterLink,
    NzBadgeModule,
    NzTagComponent,
    NzDropDownDirective,
    NzDropdownMenuComponent,
    AvatarModule,
    AvailableBusesWidgetComponent,
  ],
  template: `
    <header
      class="workspace-header"
      [class.sidebar-collapsed]="sidebarCollapsed"
    >
      <!-- Left Section: Company Info -->
      <div class="header-left">
        <!-- Mobile menu trigger: visible only below lg -->
        <button
          #mobileMenuTrigger
          type="button"
          class="mobile-menu-trigger lg:hidden flex items-center justify-center w-10 h-10 rounded-lg shrink-0 transition-colors hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-1"
          (click)="onMobileMenuOpen?.()"
          aria-label="Open menu"
        >
          <span class="fe fe-menu text-xl"></span>
        </button>
        <!-- Company Logo and Name -->
        <div class="company-info">
          <div (click)="onCompanyLogoClick?.()" class="company-logo">
            <img
              *ngIf="authUser?.company?.logoUrl"
              [src]="authUser?.company?.logoUrl || ''"
              [alt]="authUser?.company?.name || ''"
              class="logo-image"
            />
            <div *ngIf="!authUser?.company?.logoUrl" class="logo-placeholder">
              {{ getCompanyInitials() }}
            </div>
          </div>
          <div class="company-details">
            <div class="company-name">{{ authUser?.company?.name || 'Loading...' }}</div>
            <div class="company-subtitle">
              @if (authUser?.role === roles.TENANT_TERMINAL_MANAGER || 
                   authUser?.role === roles.TENANT_PARCEL_MANAGER || 
                   authUser?.role === roles.TERMINAL_PARCEL_CLERK || 
                   authUser?.role === roles.TERMINAL_BOOKING_CLERK) {
                {{ authUser?.terminalLocation }} Terminal
              } @else {
                Administrative Portal
              }
            </div>
          </div>
        </div>
      </div>

      <!-- Center Section: Command Bar & Active Operations -->
      <div class="header-center">
        <button
          class="command-bar-trigger"
          (click)="openCommandPalette()"
        >
          <span class="fe fe-search"></span>
          <span class="command-bar-text">Search or jump to...</span>
          <kbd class="command-shortcut">⌘K</kbd>
        </button>
        @if (isOperationsUser && totalActiveCount() > 0) {
          <div class="active-ops-wrapper group relative flex items-center">
            <a
              routerLink="/operations/active"
              class="active-ops-trigger flex items-center justify-center gap-1.5 min-w-[44px] h-10 rounded-xl transition-all duration-200"
              [class.has-active]="totalActiveCount() > 0"
            >
              <span class="active-ops-icon-wrap">
                <span class="fe fe-activity text-lg"></span>
              </span>
              <span class="text-sm font-semibold tabular-nums">{{ totalActiveCount() }}</span>
            </a>
            <div
              class="active-ops-dropdown absolute left-1/2 top-full -translate-x-1/2 mt-1.5 py-2 px-3 rounded-xl shadow-xl min-w-[200px] z-50 border transition-all duration-200 invisible opacity-0 group-hover:visible group-hover:opacity-100"
            >
              <div class="active-ops-dropdown-inner space-y-1.5">
                <div class="flex items-center justify-between gap-4 text-sm">
                  <span class="active-ops-label">Loading</span>
                  <span class="font-semibold tabular-nums">{{ loadingCount() }}</span>
                </div>
                <div class="flex items-center justify-between gap-4 text-sm">
                  <span class="active-ops-label">Trip in progress</span>
                  <span class="font-semibold tabular-nums">{{ inTransitCount() }}</span>
                </div>
                <a
                  routerLink="/operations/active"
                  class="active-ops-view-all mt-2 flex items-center justify-center gap-2 w-full py-2 rounded-lg text-sm font-medium transition-colors"
                >
                  View all
                  <span class="fe fe-arrow-right text-xs"></span>
                </a>
              </div>
            </div>
          </div>
        }
      </div>

      <!-- Right Section: Actions and User -->
      <div class="header-right">
        <!-- Environment Tag: hidden on mobile to free space, visible from tablet up -->
        <nz-tag
          *ngIf="envName !== 'PROD'"
          [nzColor]="envColor"
          class="env-tag hidden md:inline-flex"
        >
          {{ envName }}
        </nz-tag>

        <!-- Available Buses -->
        <tenant-available-buses-widget
          *ngIf="authUser && canSeeAvailableBuses"
          [authUser]="authUser"
        ></tenant-available-buses-widget>

        <!-- Notifications -->
        <button
          routerLink="/notification-center"
          class="header-action-button"
        >
          <nz-badge [nzCount]="unreadCount" [nzOverflowCount]="10">
            <span class="fe fe-bell"></span>
          </nz-badge>
        </button>

        <!-- Theme Selector: hidden on mobile (<768px), shown in drawer instead -->
        <button
          [nzDropdownMenu]="themeMenu"
          nz-dropdown
          [nzPlacement]="'bottomRight'"
          class="header-action-button hidden md:flex"
          [attr.aria-label]="'Theme: ' + themeService.getThemeModeLabel()"
        >
          <span class="fe {{ themeService.getThemeIconClass() }}"></span>
        </button>

        <!-- User Profile: hidden on mobile (<768px), user info is in drawer -->
        <div
          [nzDropdownMenu]="userMenu"
          nz-dropdown
          [nzPlacement]="'bottomRight'"
          class="user-profile-dropdown hidden md:flex"
        >
          <div class="user-profile">
            <ngx-avatars
              [name]="authUser?.name || ''"
              [size]="32"
              class="user-avatar"
            ></ngx-avatars>
            <div class="user-info" *ngIf="!sidebarCollapsed">
              <div class="user-name">{{ authUser?.name }}</div>
              <div class="user-role">{{ authUser?.role }}</div>
            </div>
          </div>
        </div>

        <nz-dropdown-menu #userMenu="nzDropdownMenu">
          <div class="user-menu">
            <a routerLink="/profile" class="user-menu-item">
              <span class="fe fe-user mr-2"></span>
              Manage Account
            </a>
            <button (click)="logout()" class="user-menu-item user-menu-item-danger">
              <span class="fe fe-log-out mr-2"></span>
              Sign out
            </button>
          </div>
        </nz-dropdown-menu>

        <nz-dropdown-menu #themeMenu="nzDropdownMenu">
          <div class="theme-menu">
            <button type="button" class="theme-menu-item" (click)="setTheme('system')">
              <span class="fe fe-monitor mr-2"></span>
              <span>System (Recommended)</span>
              @if (themeService.themeMode() === 'system') {
                <span class="fe fe-check ml-auto"></span>
              }
            </button>
            <button type="button" class="theme-menu-item" (click)="setTheme('light')">
              <span class="fe fe-sun mr-2"></span>
              <span>Light</span>
              @if (themeService.themeMode() === 'light') {
                <span class="fe fe-check ml-auto"></span>
              }
            </button>
            <button type="button" class="theme-menu-item" (click)="setTheme('dark')">
              <span class="fe fe-moon mr-2"></span>
              <span>Dark</span>
              @if (themeService.themeMode() === 'dark') {
                <span class="fe fe-check ml-auto"></span>
              }
            </button>
          </div>
        </nz-dropdown-menu>
      </div>
    </header>
  `,
  styles: [
    `
      :host {
        display: block;
        width: 100%;
        --active-ops-bg: rgba(56, 189, 248, 0.08);
        --active-ops-bg-hover: rgba(56, 189, 248, 0.16);
      }

      .workspace-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 12px 24px;
        background-color: var(--header-bg);
        backdrop-filter: var(--header-backdrop);
        -webkit-backdrop-filter: var(--header-backdrop);
        border-bottom: 1px solid var(--header-border);
        min-height: 70px; 
        gap: 24px;

        .header-left {
          flex: 0 0 auto;
          display: flex;
          align-items: center;
          gap: 12px;
          min-width: 0;

          .company-info {
            display: flex;
            align-items: center;
            gap: 12px;
            min-width: 0;

            .company-logo {
              width: 40px;
              height: 40px;
              border-radius: 8px;
              overflow: hidden;
              cursor: pointer;
              transition: transform var(--transition-fast);
              flex-shrink: 0;

              &:hover {
                transform: scale(1.05);
              }

              .logo-image {
                width: 100%;
                height: 100%;
                object-fit: cover;
              }

              .logo-placeholder {
                width: 100%;
                height: 100%;
                display: flex;
                align-items: center;
                justify-content: center;
                background: var(--accent-primary);
                color: white;
                font-weight: 600;
                font-size: 14px;
              }
            }

            .company-details {
              min-width: 0;

              .company-name {
                font-size: 16px;
                font-weight: 600;
                color: var(--header-text);
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
              }

              .company-subtitle {
                font-size: 12px;
                color: var(--header-text-secondary);
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
              }
            }
          }
        }

        .header-center {
          flex: 1;
          display: flex;
          justify-content: center;
          max-width: 600px;
          margin: 0 auto;
          gap: 8px;

          .command-bar-trigger {
            display: flex;
            align-items: center;
            width: 100%;
            max-width: 500px;
            padding: 10px 16px;
            background: var(--header-search-bg);
            border: 1px solid var(--header-search-border);
            border-radius: 8px;
            cursor: pointer;
            transition: all var(--transition-fast);
            gap: 12px;

            &:hover {
              background: var(--header-search-hover);
              border-color: var(--accent-primary);
            }

            .fe-search {
              font-size: 18px;
              color: var(--header-search-icon);
              flex-shrink: 0;
            }

            .command-bar-text {
              flex: 1;
              text-align: left;
              color: var(--header-search-text);
              font-size: 14px;
            }

            .command-shortcut {
              background: var(--header-bg);
              border: 1px solid var(--header-search-border);
              border-radius: 4px;
              padding: 2px 6px;
              font-size: 11px;
              color: var(--header-search-text);
              font-family: monospace;
              flex-shrink: 0;
            }
          }

          .active-ops-wrapper {
            .active-ops-trigger {
              cursor: pointer;
              border: none;
              background: var(--active-ops-bg);
              color: var(--accent-primary);
              text-decoration: none;
              box-shadow: 0 0 0 1px rgba(56, 189, 248, 0.22), 0 2px 8px rgba(56, 189, 248, 0.06);
              outline: none;

              &:hover {
                background: var(--active-ops-bg-hover);
                box-shadow: 0 0 0 1px rgba(56, 189, 248, 0.35), 0 4px 12px rgba(56, 189, 248, 0.1);
              }

              .active-ops-icon-wrap {
                display: inline-flex;
                align-items: center;
                justify-content: center;
                transform-origin: center;
              }

              &.has-active .active-ops-icon-wrap {
                animation: active-ops-breathe 1.8s ease-in-out infinite;
                will-change: transform;
              }

              &.has-active .active-ops-icon-wrap .fe {
                animation: active-ops-glow 1.8s ease-in-out infinite;
                will-change: opacity, filter;
              }
            }

            .active-ops-dropdown {
              background: var(--header-bg);
              border-color: var(--header-border);

              .active-ops-dropdown-inner {
                color: var(--header-text);
              }

              .active-ops-label {
                color: var(--header-text-secondary);
              }
            }

            .active-ops-view-all {
              color: var(--accent-primary);
              text-decoration: none;

              &:hover {
                background: var(--sidebar-hover);
              }
            }
          }
        }

        @keyframes active-ops-breathe {
          0%,
          100% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.22);
          }
        }

        @keyframes active-ops-glow {
          0%,
          100% {
            opacity: 1;
            filter: brightness(1);
          }
          50% {
            opacity: 0.88;
            filter: brightness(1.4);
          }
        }

        .header-right {
          flex: 0 0 auto;
          display: flex;
          align-items: center;
          gap: 12px;

          .env-tag {
            margin: 0;
          }

          .header-action-button {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 40px;
            height: 40px;
            border: none;
            background: transparent;
            border-radius: 8px;
            cursor: pointer;
            transition: all var(--transition-fast);
            color: var(--header-action);

            &:hover {
              background: var(--header-action-hover);
              color: var(--header-text);
            }

            .fe {
              font-size: 18px;
            }
          }

          .user-profile-dropdown {
            cursor: pointer;

            .user-profile {
              display: flex;
              align-items: center;
              gap: 10px;
              padding: 4px 8px;
              border-radius: 8px;
              transition: background var(--transition-fast);

              &:hover {
                background: var(--header-action-hover);
              }

              .user-info {
                min-width: 0;

                .user-name {
                  font-size: 14px;
                  font-weight: 500;
                  color: var(--header-text);
                  white-space: nowrap;
                  overflow: hidden;
                  text-overflow: ellipsis;
                }

                .user-role {
                  font-size: 12px;
                  color: var(--header-text-secondary);
                  white-space: nowrap;
                  overflow: hidden;
                  text-overflow: ellipsis;
                }
              }
            }
          }
        }

      }

      .user-menu {
        padding: 4px;
        min-width: 200px;
        background-color: var(--header-bg);
        border-radius: 8px;
        box-shadow: var(--shadow-lg);
        border: 1px solid var(--header-border);

        .user-menu-item {
          display: flex;
          align-items: center;
          width: 100%;
          padding: 10px 12px;
          border: none;
          background: none;
          color: var(--header-text);
          text-decoration: none;
          font-size: 14px;
          border-radius: 6px;
          cursor: pointer;
          transition: all var(--transition-fast);

          &:hover {
            background-color: var(--sidebar-hover);
          }

          &.user-menu-item-danger {
            color: var(--error);

            &:hover {
              background-color: rgba(239, 68, 68, 0.1);
            }
          }
        }
      }

      .theme-menu {
        padding: 4px;
        min-width: 210px;
        background-color: var(--header-bg);
        border-radius: 8px;
        box-shadow: var(--shadow-lg);
        border: 1px solid var(--header-border);

        .theme-menu-item {
          display: flex;
          align-items: center;
          width: 100%;
          padding: 10px 12px;
          border: none;
          background: none;
          color: var(--header-text);
          text-decoration: none;
          font-size: 14px;
          border-radius: 6px;
          cursor: pointer;
          transition: all var(--transition-fast);

          &:hover {
            background-color: var(--header-action-hover);
          }
        }
      }

      @media (max-width: 1024px) {
        .workspace-header {
          padding-left: 16px !important;

          .header-center {
            display: none;
          }

          .user-profile .user-info {
            display: none;
          }
        }
      }
    `,
  ],
})
export class WorkspaceHeaderComponent {
  @Input() authUser: TenantUsersDto | null = null;
  @Input() sidebarCollapsed = false;
  @Input() onlineUsers = 0;
  @Input() tenantPresence: TenantPresence[] = [];
  @Input() unreadCount = 0;
  @Input() envName = 'PROD';
  @Input() envColor = 'default';
  @Input() onCompanyLogoClick?: () => void;
  @Input() onLogout?: () => void;
  @Input() onMobileMenuOpen?: () => void;

  @ViewChild('mobileMenuTrigger', { read: ElementRef })
  mobileMenuTriggerRef?: ElementRef<HTMLButtonElement>;

  roles = TenantUserRoles;

  /** Focus the mobile menu trigger (e.g. after closing the drawer for accessibility). */
  focusMobileMenuTrigger(): void {
    this.mobileMenuTriggerRef?.nativeElement?.focus();
  }

  themeService = inject(ThemeService);
  private commandPaletteService = inject(CommandPaletteService);
  private operationsApi = inject(OperationsApi);
  private context = new HttpContext().set(NGX_LOADING_BAR_IGNORED, true);
  private refetchInterval = 7000;

  loadingOperationsQuery = injectQuery(() => ({
    queryKey: [CacheKeysEnum.LOADING_OPERATIONS_GROUPED_BY_DATE, 'sidebar-loading'],
    queryFn: () =>
      lastValueFrom(this.operationsApi.operationsControllerGetLoadingOperationGroupedByDate(undefined, this.context)),
    select: (data: GroupedOperationsDto[]) => data,
    refetchInterval: this.refetchInterval,
    refetchIntervalInBackground: true,
  }));

  inTransitOperationsQuery = injectQuery(() => ({
    queryKey: [CacheKeysEnum.IN_TRANSIT_OPERATIONS_GROUPED_BY_DATE, 'sidebar-in-transit'],
    queryFn: () =>
      lastValueFrom(this.operationsApi.operationsControllerGetInTransitOperationGroupedByDate(undefined, this.context)),
    select: (data: GroupedOperationsDto[]) => data,
    refetchInterval: this.refetchInterval,
    refetchIntervalInBackground: true,
  }));

  loadingCount = computed(() => {
    const data = this.loadingOperationsQuery.data();
    if (!data) return 0;
    return data.reduce((acc, group) => acc + (group.operations?.length || 0), 0);
  });

  inTransitCount = computed(() => {
    const data = this.inTransitOperationsQuery.data();
    if (!data) return 0;
    return data.reduce((acc, group) => acc + (group.operations?.length || 0), 0);
  });

  totalActiveCount = computed(() => this.loadingCount() + this.inTransitCount());

  openCommandPalette() {
    this.commandPaletteService.open();
  }

  setTheme(mode: ThemeMode) {
    this.themeService.setTheme(mode);
  }

  logout() {
    if (this.onLogout) {
      this.onLogout();
    }
  }

  getCompanyInitials(): string {
    if (!this.authUser?.company?.name) {
      return 'BP';
    }
    return this.authUser.company.name
      .split(' ')
      .map((word) => word[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  }

  /** The server refuses these roles the list, and the widget polled it into a pop-up every 5 seconds. */
  get canSeeAvailableBuses(): boolean {
    const role = this.authUser?.role as TenantUserRoles | undefined;
    return ![
      this.roles.BUS_STOP_MANAGER,
      this.roles.BUS_STOP_CLERK,
      this.roles.TENANT_DIRECTOR,
      this.roles.TENANT_ACCOUNTANT,
      this.roles.TENANT_MANAGEMENT_MEMBER,
    ].includes(role as TenantUserRoles);
  }

  get isOperationsUser(): boolean {
    const role = this.authUser?.role as TenantUserRoles | undefined;
    if (!role) {
      return false;
    }
    return (
      role === this.roles.TENANT_ADMIN ||
      role === this.roles.TENANT_TERMINAL_MANAGER ||
      role === this.roles.TENANT_PARCEL_MANAGER ||
      role === this.roles.TERMINAL_BOOKING_CLERK
    );
  }
}
